import React from 'react';

import { postJson } from '../utils/fetch';
import asDataProvider, { type ResultComplete, Status } from './as-data-provider';
import {
	type CollaborationGraphContainersResponse,
	Permissions,
	type UserPermissionResponse,
	type WithCloudId,
	type ProviderResults,
} from '../../types';
import { withCached } from '../utils/with-cached';
import withHandleOptionalCloudId from './with-handle-optional-cloud-id';
import { ExperienceMark } from '../utils/experience-tracker';

const fetchCollaborationGraphRecentContainers = ({ cloudId }: WithCloudId) =>
	postJson<CollaborationGraphContainersResponse>(
		'/gateway/api/collaboration/v1/collaborationgraph/user/container',
		{
			containerTypes: ['confluenceSpace', 'jiraProject'],
			context: {
				contextType: 'atlassianSwitcher',
				principalId: 'context',
				siteId: cloudId,
			},
			expanded: true,
			maxNumberOfResults: 6,
			userId: 'context',
		},
	);

const RealCollaborationGraphRecentContainersProvider = asDataProvider(
	'collaborationGraphRecentContainers',
	ExperienceMark.API_COLLABORATION_GRAPH_RECENT_CONTAINERS,
	fetchCollaborationGraphRecentContainers,
);

export const emptyCollaborationGraphRecentContainers: ResultComplete<CollaborationGraphContainersResponse> =
	{
		status: Status.COMPLETE,
		data: { collaborationGraphEntities: [] },
	};

export const nullCollaborationGraphRecentContainers: ResultComplete<CollaborationGraphContainersResponse> =
	{
		status: Status.COMPLETE,
		data: { collaborationGraphEntities: null },
	};

export const CollaborationGraphRecentContainersProvider = withHandleOptionalCloudId(
	({
		cloudId,
		enableRecentContainers,
		children,
	}: {
		cloudId: string;
		enableRecentContainers?: boolean;
		children: (
			collaborationGraphRecentContainers: ProviderResults['collaborationGraphRecentContainers'],
		) => React.ReactNode;
	}) => {
		if (enableRecentContainers) {
			return (
				<RealCollaborationGraphRecentContainersProvider cloudId={cloudId}>
					{children}
				</RealCollaborationGraphRecentContainersProvider>
			);
		}

		return <React.Fragment>{children(emptyCollaborationGraphRecentContainers)}</React.Fragment>;
	},
	emptyCollaborationGraphRecentContainers.data,
);

// Permissions api
type FetchPermissionParamsType = WithCloudId & {
	permissionId: Permissions;
};
const fetchPermission = withCached(({ cloudId, permissionId }: FetchPermissionParamsType) =>
	postJson<UserPermissionResponse>(`/gateway/api/permissions/permitted`, {
		permissionId,
		resourceId: `ari:cloud:platform::site/${cloudId}`,
	}).then((permission) => permission.permitted),
);

export const UserPermissionProvider = withHandleOptionalCloudId(
	asDataProvider(
		'permission',
		ExperienceMark.API_PERMISSIONS,
		fetchPermission,
		fetchPermission.cached,
	),
	false,
);

export const prefetchAll = ({ cloudId }: WithCloudId) => {
	fetchPermission({ cloudId, permissionId: Permissions.MANAGE });
};

export const resetAll = () => {
	fetchPermission.reset();
};
